import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry } from "@/lib/sentry";

/**
 * Top-level error boundary (parity with iOS react-error-boundary).
 *
 * Without this, a single render-time crash anywhere in the tree unmounts the
 * whole app → blank white screen with no recovery and (pre-Sentry) no report.
 * React swallows render errors, so the global window.onerror handler never sees
 * them — they MUST be caught here and forwarded to Sentry.
 *
 * Renders a minimal recovery UI (plain inline styles so it works even if the
 * theme/provider tree is what crashed) with a Reload action.
 */
interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
     
    console.error("[ErrorBoundary] render crash:", error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: 24,
          textAlign: "center",
          background: "#f7f7f8",
          color: "#1a1a1a",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div style={{ fontSize: 40 }}>🦜</div>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: 14, color: "#555", maxWidth: 320, margin: 0 }}>
          The app hit an unexpected error. Reloading usually fixes it — your
          progress is saved.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            marginTop: 8,
            padding: "12px 28px",
            borderRadius: 999,
            border: "none",
            background: "#0c92f1",
            color: "#fff",
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
